import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, mergeProps as _mergeProps, createTextVNode as _createTextVNode, withModifiers as _withModifiers } from "vue"

import { ref, onMounted, watchEffect, toRaw } from 'vue';
import { useRoute } from 'vue-router';
import { configRegistry } from '@/lib/configRegistry';
import { remoteOptionsRegistry } from '@/lib/remoteOptions';
import { useFloatFormStore } from '@/store/floatForm';
import type { FormField, FormProps } from '@/types/form';


export default /*@__PURE__*/_defineComponent({
  __name: 'EditForm',
  setup(__props) {

const loading = ref(true);
const is_saving = ref(false);

const route = useRoute();
const moduleName = (route.meta.moduleName ?? route.name) as string;
const floatFormStore = useFloatFormStore();
const props = floatFormStore.props as FormProps;
const onUpdate = props?.onUpdate;
const item = props?.item || {};

const formFields = ref<FormField[]>([]);
const form = ref<Record<string, any>>({});
const formValid = ref(true);
const formRef = ref();
const visibleFields = ref<FormField[]>([]);

watchEffect(() => {
  visibleFields.value = formFields.value
    .map((field) => {
      const val = form.value[field.dependsOn as any];
      const condition = field.dependsCondition;
      let conditionMet = true;

      if (field.dependsOn && field.dependsBehavior) {
        switch (condition?.condition) {
          case 'diferent':
            conditionMet = val !== undefined && val !== '' && val !== condition.value;
            break;
          case 'equals':
            conditionMet = val === condition.value;
            break;
          case 'includes':
            conditionMet = !JSON.stringify(val)?.toLowerCase?.().includes(condition.value);
          break;
        }

        if (field.dependsBehavior === 'hidden' && !conditionMet) return null;
        if (field.dependsBehavior === 'disabled' && !conditionMet) {
          return { ...field, permission: 'disabled' };
        }
      }

      if (field.permission === 'hidden') return null;

      return field;
    })
    .filter((f): f is FormField => !!f);
});

const getFieldProps = (field: FormField) => {
  const { children, component, value, transform, parse, ...rest } = field;
  const props: Record<string, any> = {
    ...rest,
    label: field.label,
    clearable: true,
    density: 'compact',
    rules: field.required ? [(v: any) => !!v || 'Campo obligatorio'] : [],
    'item-title': field.itemText || 'label',
    'item-value': field.itemValue || 'value',
    disabled: field.permission === 'disabled',
    readonly: field.readonly,
    'prepend-inner-icon': field.icon,
  };

  if (field.options) props.items = field.options;

  if (field.fetchOptions && remoteOptionsRegistry[field.fetchOptions]) {
    props['onUpdate:search'] = async (val: string) => {
      if (!val || val.length < 2) return;
      const fetchFn = remoteOptionsRegistry[field.fetchOptions!];
      const results = await fetchFn(val);
      field.options = results;
      props.items = results;
    };
  }

  return props;
};

const flattenForm = (form: Record<string, any>, fields: FormField[]) => {
  const flatForm: Record<string, any> = { id: form.id };

  for (const field of fields) {
    const val = form[field.value];

    if (typeof field.transform === 'function') {
      const result = field.transform(toRaw(val), form);
      if (typeof result === 'object' && !Array.isArray(result)) {
        Object.assign(flatForm, result);
      } else {
        flatForm[field.value] = result;
      }
      continue;
    }

    if (field.isJsonArray && field.children) {
      if(field.plainJson){
        for (const child of field.children) {
          flatForm[child.value] = val?.[child.value] ?? null;
        }
      }else{
        flatForm[field.value] = toRaw(val);
      }
    } else if (field.multiple && Array.isArray(val)) {
      flatForm[field.value] = val.map((v: any) =>
        typeof v === 'object' ? v[field.itemValue || 'value'] ?? v.id ?? null : v
      );
    } else if (typeof val === 'object' && val !== null) {
      flatForm[field.value] = val[field.itemValue || 'value'] ?? val.id ?? null;
    } else {
      flatForm[field.value] = val ?? null;
    }
  }

  return flatForm;
};

const resetForm = () => {
  formFields.value.forEach(f => (form.value[f.value] = null));
};

const closeForm = () => {
  resetForm();
  floatFormStore.closeForm();
};

const submitForm = async () => {
  if (!formRef.value?.validate()) return;
  is_saving.value = true;
  try {
    const payload = flattenForm(form.value, formFields.value);
    payload.id = item.id;

    if (onUpdate) {
      await onUpdate(payload);
    } else {
      console.warn('[EditForm] No se proporcionó función onUpdate');
    }

    closeForm();
    is_saving.value = false;
  } catch (err) {
    is_saving.value = false;
    console.error('[EditForm] Error en onUpdate', err);
  }
};

onMounted(async () => {
  const loader = configRegistry[moduleName];
  if (!loader) {
    console.error(`[EditForm] Config para ${moduleName} no encontrada.`);
    return;
  }

  try {
    const mod = await loader();
    formFields.value = mod.EDIT_FORM || [];

    for (const f of formFields.value) {
      let raw = item[f.value] ?? item.attributes?.[f.value];
      if (f.isJsonArray && f.plainJson && f.children) {
        const group: Record<string, any> = {};
        for (const child of f.children) {
          group[child.value] = item[child.value] ?? item.attributes?.[child.value] ?? null;
        }
        raw = group;
      }
      // ✅ Aplica parse si está definido en el config
      if (typeof f.parse === 'function') {
        form.value[f.value] = f.parse(raw, item);

        continue;
      }
      // ✅ Caso general
      form.value[f.value] = raw ?? null;

      // ✅ Si tiene fetchOptions, buscar y asignar opción completa
      /*if (f.fetchOptions && remoteOptionsRegistry[f.fetchOptions]) {
        const result = await remoteOptionsRegistry[f.fetchOptions]();
        f.options = result;
        const itemValueKey = f.itemValue || 'value';
        if (Array.isArray(form.value[f.value])) {
          form.value[f.value] = form.value[f.value].map((v: any) =>
            typeof v === 'object' ? v : result.find(opt => opt[itemValueKey] === v) || v
          );
        } else {
          const val = form.value[f.value];
          if (val && typeof val !== 'object') {
            const match = result.find(opt => opt[itemValueKey] === val);
            if (match) form.value[f.value] = match;
          }
        }
      }*/
    }
    loading.value = false;
  } catch (err) {
    console.error('[EditForm] Error al cargar config:', err);
  }
});

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createBlock(_component_v_form, {
    onSubmit: _withModifiers(submitForm, ["prevent"]),
    ref_key: "formRef",
    ref: formRef,
    modelValue: formValid.value,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formValid).value = $event))
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, { class: "px-5 py-3" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center mb-3" }, {
            default: _withCtx(() => _cache[1] || (_cache[1] = [
              _createElementVNode("span", null, "Editar", -1)
            ])),
            _: 1
          }),
          _createVNode(_component_v_card_text, null, {
            default: _withCtx(() => [
              (loading.value)
                ? (_openBlock(), _createBlock(_component_v_row, {
                    key: 0,
                    class: "ma-12"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_spacer),
                      _createVNode(_component_v_progress_circular, { indeterminate: "" }),
                      _createVNode(_component_v_spacer)
                    ]),
                    _: 1
                  }))
                : (_openBlock(), _createBlock(_component_v_row, { key: 1 }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleFields.value, (field) => {
                        return (_openBlock(), _createBlock(_component_v_col, {
                          key: field.value,
                          cols: field.col || 12
                        }, {
                          default: _withCtx(() => [
                            (!field.isJsonArray)
                              ? (_openBlock(), _createBlock(_resolveDynamicComponent(field.component), _mergeProps({
                                  key: 0,
                                  modelValue: form.value[field.value],
                                  "onUpdate:modelValue": ($event: any) => ((form.value[field.value]) = $event),
                                  ref_for: true
                                }, getFieldProps(field), {
                                  rules: field.required ? [v => !!v || 'Campo obligatorio'] : []
                                }), null, 16, ["modelValue", "onUpdate:modelValue", "rules"]))
                              : (_openBlock(), _createBlock(_resolveDynamicComponent(field.component), {
                                  key: 1,
                                  modelValue: form.value[field.value],
                                  "onUpdate:modelValue": ($event: any) => ((form.value[field.value]) = $event),
                                  fields: field.children,
                                  summary: field.summary,
                                  label: field.label
                                }, null, 8, ["modelValue", "onUpdate:modelValue", "fields", "summary", "label"]))
                          ]),
                          _: 2
                        }, 1032, ["cols"]))
                      }), 128))
                    ]),
                    _: 1
                  }))
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_actions, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_spacer),
              _createVNode(_component_v_btn, {
                variant: "text",
                onClick: closeForm
              }, {
                default: _withCtx(() => _cache[2] || (_cache[2] = [
                  _createTextVNode("Cancelar")
                ])),
                _: 1
              }),
              _createVNode(_component_v_btn, {
                type: "submit",
                disabled: !formValid.value,
                loading: is_saving.value,
                color: "secondary"
              }, {
                default: _withCtx(() => _cache[3] || (_cache[3] = [
                  _createTextVNode("Guardar")
                ])),
                _: 1
              }, 8, ["disabled", "loading"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})